// 儀表類圖表共用的數值分段配色。
// 自給率與電池電量都是「越高越好」的百分比指標，共用同一組分段，
// 使用者才不必為每張卡重新學一套顏色語意。

const BANDS = [
    { upTo: 40, color: '#D18800' }, // 偏低
    { upTo: 70, color: '#4277C2' }, // 中等
    { upTo: Infinity, color: '#11caa0' }, // 良好
]

export const IDLE_COLOR = '#c4c9cf'

export function levelColor(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return IDLE_COLOR
    return BANDS.find((band) => Number(value) < band.upTo).color
}

// ---------------------------------------------------------------------------
// 環境品質分級（PM2.5、二氧化碳、濁度）
//
// 上面那組是「越高越好」，環境品質三項相反，越低越好，而且級距寬度差很多
// （PM2.5 的良好只有 12.4 寬，對所有族群不健康卻橫跨 75）。若照實際數值
// 線性配置，平時的個位數讀值會讓指針永遠貼在最左端，看不出落在哪一級。
// 因此各級在弧上等寬，指針位置由「第幾級 + 該級內的比例」決定，
// 與桃園市空氣儀表板的呈現方式一致。
//
// 配色依 PM 2026-09-14 提供的色票對照表，取代原本以環境部 AQI 官方色為底的那組。
// color 是弧段顏色；不是讀值所在那一級的弧段，由 GaugeChart 以 45% 透明度淡化。
// ink 是讀值所在那一級的指針、數字與級名顏色。
// inkDim 是其他級的級名顏色。對照表上這欄標 55%，但色碼並不是 ink 疊 55%
// 算得出來的（差 15～38），所以直接寫死色碼，畫面才會跟對照表一致。
// level 對應語系檔的 quality-level.*。
// ---------------------------------------------------------------------------

// 分級儀表的畫布範圍（SVG viewBox）。GaugeChart 畫圖用，WeatherCard 放大時
// 也要用它的寬高比算左欄寬度，讓儀表剛好上下撐滿、跟右邊的趨勢圖一樣高。
// 範圍是用瀏覽器實際量出來的：中英文兩種標籤、CO2 與 PM2.5 兩組分級、
// 最長的數值與級名，全部內容的邊界是 x 24.2～248.3、y 16.3～164.5，
// 左右以圓心 130 對稱再各留 1.5，所以四周幾乎沒有空白。
// 改了 GaugeChart 的字級或標籤半徑，這組數字要重量。
export const BAND_VIEWBOX = { x: 10, y: 15, width: 240, height: 151 }

export const QUALITY_BANDS = {
    // 環境部 AQI 的 PM2.5 副指標分級。空氣品質標準於 2023-09-30 修正後，
    // AQI 分級自 2025-01-01 起跟著調整，這裡採用調整後的級距。
    pm25: [
        { from: 0, to: 12.4, color: '#356F65', ink: '#285A52', inkDim: '#739C91', level: 'good' },
        { from: 12.5, to: 30.4, color: '#D6B84B', ink: '#8A760F', inkDim: '#B8A760', level: 'moderate' },
        { from: 30.5, to: 50.4, color: '#D8894A', ink: '#9B5C24', inkDim: '#C39261', level: 'sensitive' },
        { from: 50.5, to: 125.4, color: '#C65D6B', ink: '#A83F4D', inkDim: '#D1818C', level: 'unhealthy' },
        { from: 125.5, to: 225.4, color: '#8A67A6', ink: '#6F4A8C', inkDim: '#AA8CBD', level: 'very-unhealthy' },
        { from: 225.5, to: 325.4, color: '#8E5964', ink: '#74434D', inkDim: '#B98791', level: 'hazardous' },
    ],
    // 二氧化碳不在 AQI 的六項污染物內，沒有官方分級，改用法規值切分：
    // 1000 ppm 是室內空氣品質標準的 8 小時值；2000～5000 ppm 是文獻上
    // 開始影響專注力與認知的區間；5000 ppm 是勞工作業場所的 8 小時
    // 日時量平均容許濃度。
    co2: [
        { from: 0, to: 1000, color: '#356F65', ink: '#285A52', inkDim: '#739C91', level: 'good' },
        { from: 1000, to: 2000, color: '#D6B84B', ink: '#8A760F', inkDim: '#B8A760', level: 'moderate' },
        { from: 2000, to: 5000, color: '#D8894A', ink: '#9B5C24', inkDim: '#C39261', level: 'poor' },
        { from: 5000, to: 10000, color: '#C65D6B', ink: '#A83F4D', inkDim: '#D1818C', level: 'danger' },
    ],
    // 濁度同樣沒有分級制度，用飲用水水質標準的三段濁度限值當級距：
    // 一般情況 4 NTU；水源濁度 500～1500 NTU 時放寬到 10 NTU；
    // 水源濁度超過 1500 NTU 時放寬到 30 NTU。超過 30 即無論何種情況都超標。
    // 站上實測值長期在 1.4～2.2 NTU，落在第一級，級距切在這個尺度才有解析度；
    // 若日後改測原水（動輒上千 NTU），這組級距要整組換掉。
    turbidity: [
        { from: 0, to: 4, color: '#356F65', ink: '#285A52', inkDim: '#739C91', level: 'good' },
        { from: 4, to: 10, color: '#D6B84B', ink: '#8A760F', inkDim: '#B8A760', level: 'moderate' },
        { from: 10, to: 30, color: '#D8894A', ink: '#9B5C24', inkDim: '#C39261', level: 'poor' },
        { from: 30, to: 100, color: '#C65D6B', ink: '#A83F4D', inkDim: '#D1818C', level: 'over-limit' },
    ],
}

/**
 * 找出數值落在哪一級。超出最後一級一律歸入最後一級。
 *
 * @param {number|null} value 讀值
 * @param {Array} bands 分級表
 * @returns {number} 級數索引；無值時回傳 -1
 */
export function bandIndexOf(value, bands) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return -1
    const n = Number(value)
    const idx = bands.findIndex((band) => n <= band.to)
    return idx === -1 ? bands.length - 1 : idx
}
